import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

export default function WhiteLabelSettings() {
  const [settings, setSettings] = useState({ company_name: '', primary_color: '#5865f2', secondary_color: '#7c3aed', custom_domain: '', email_from_name: '', email_from_address: '', footer_text: '', hide_branding: false });
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    api.get('/tenant/white-label')
      .then(r => { if (r.data) setSettings(s => ({ ...s, ...r.data })); })
      .catch(err => { if (err.response?.status !== 404) toast.error(err.response?.data?.error || 'Failed'); })
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/tenant/white-label', settings);
      setSettings(s => ({ ...s, ...r.data }));
      toast.success('White-label settings saved');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const verifyDomain = async () => {
    try {
      await api.post('/tenant/white-label/verify-domain');
      toast.success('Domain verified');
      setSettings(s => ({ ...s, domain_verified: true }));
    } catch (err) { toast.error(err.response?.data?.error || 'Verification failed'); }
  };

  const set = (k, v) => setSettings(s => ({ ...s, [k]: v }));
  const S = { label: { fontSize: 13, color: 'var(--text-2)', display: 'block', marginBottom: 5 }, card: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginBottom: 16 } };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>Loading…</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600 }}>White-Label Settings</h2>
        <button onClick={save} disabled={saving} className="btn btn-primary">{saving ? 'Saving…' : 'Save changes'}</button>
      </div>

      <div style={S.card}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Branding</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <label><span style={S.label}>Company name</span><input value={settings.company_name || ''} onChange={e => set('company_name', e.target.value)} className="input" style={{ width: '100%' }} /></label>
          <label><span style={S.label}>Logo URL</span><input value={settings.logo_url || ''} onChange={e => set('logo_url', e.target.value)} placeholder="https://..." className="input" style={{ width: '100%' }} /></label>
          <label>
            <span style={S.label}>Primary colour</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="color" value={settings.primary_color || '#5865f2'} onChange={e => set('primary_color', e.target.value)} style={{ width: 40, height: 36, border: 'none', borderRadius: 6, cursor: 'pointer' }} />
              <input value={settings.primary_color || '#5865f2'} onChange={e => set('primary_color', e.target.value)} className="input" style={{ width: '100%' }} />
            </div>
          </label>
          <label>
            <span style={S.label}>Secondary colour</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="color" value={settings.secondary_color || '#7c3aed'} onChange={e => set('secondary_color', e.target.value)} style={{ width: 40, height: 36, border: 'none', borderRadius: 6, cursor: 'pointer' }} />
              <input value={settings.secondary_color || '#7c3aed'} onChange={e => set('secondary_color', e.target.value)} className="input" style={{ width: '100%' }} />
            </div>
          </label>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!settings.hide_branding} onChange={e => set('hide_branding', e.target.checked)} />
          <span style={{ fontSize: 13 }}>Hide "Powered by RechargeKit" branding</span>
        </label>
      </div>

      <div style={S.card}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Custom Domain</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <span style={S.label}>Domain</span>
            <input value={settings.custom_domain || ''} onChange={e => set('custom_domain', e.target.value)} placeholder="docs.yourcompany.com" className="input" style={{ width: '100%' }} />
          </div>
          {settings.domain_verified
            ? <span style={{ color: '#a6e3a1', fontSize: 13, paddingBottom: 8 }}>✓ Verified</span>
            : <button onClick={verifyDomain} className="btn btn-ghost" style={{ marginBottom: 0 }}>Verify</button>
          }
        </div>
        {settings.custom_domain && !settings.domain_verified && (
          <div style={{ marginTop: 10, padding: 12, background: 'var(--bg)', borderRadius: 8, fontSize: 12, color: 'var(--text-3)' }}>
            Add a CNAME record pointing <code style={{ color: 'var(--text-2)' }}>{settings.custom_domain}</code> → <code style={{ color: 'var(--text-2)' }}>docs.apidog.app</code>
          </div>
        )}
      </div>

      <div style={S.card}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Email Settings</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <label><span style={S.label}>From name</span><input value={settings.email_from_name || ''} onChange={e => set('email_from_name', e.target.value)} placeholder="Acme Support" className="input" style={{ width: '100%' }} /></label>
          <label><span style={S.label}>From address</span><input value={settings.email_from_address || ''} onChange={e => set('email_from_address', e.target.value)} placeholder="support@acme.com" className="input" style={{ width: '100%' }} /></label>
        </div>
        <label style={{ display: 'block', marginTop: 14 }}><span style={S.label}>Footer text</span><textarea value={settings.footer_text || ''} onChange={e => set('footer_text', e.target.value)} className="input" style={{ width: '100%', height: 60, resize: 'vertical' }} /></label>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={save} disabled={saving} className="btn btn-primary">{saving ? 'Saving…' : 'Save all changes'}</button>
      </div>
    </div>
  );
}
